import { useState, useEffect } from "react";
import { BookOpen, LogOut, ChevronDown, Menu, X, MessageSquare } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/images/ecom_tamil_logo.png";
import LogoutModal from "./LogoutModal";

export default function CourseHeader({ activeNavItem }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation(); 

  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setLoggedInUser({
          name: parsedUser.userName || parsedUser.name || "User",  // Prioritize 'name' field
          email: parsedUser.email || "user@example.com",
          role: parsedUser.role || "Student",
          avatar: parsedUser.avatar || null
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
        setDefaultUser();
      }
    } else {
      setDefaultUser();
    }
  }, []);

  const setDefaultUser = () => {
    setLoggedInUser({
      name: "User",
      email: "user@example.com",
      role: "Student",
      avatar: null
    });
  };

  const userType = localStorage.getItem('usertype');

  const adminMenuItems = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/admin/courseslist', label: 'Course List' },
    { path: '/newsletter', label: 'News Letter' },
    { path: '/service', label: 'Services Summary' },
    { path: '/webinar', label: 'Webinar' },
    { path: '/webinarregistration', label: 'Webinar Registration' },
    { path: '/completedservice', label: 'Completed Service' },
    { path: '/paymenthistory', label: 'Course Payment history' },
    { path: '/webinarpaymenthistory', label: 'Webinar Payment history' },
  ];

  const menuItems = userType === "1"
    ? adminMenuItems
    : userType === "3" 
      ? [
          { path: "/solutions", label: "Solutions" },
          { path: "/completedsolutions", label: "Completed Solutions" }
        ]
      : [
          { path: "/courses", label: "Course List" },
          { path: "/mycourse", label: "My Course" },
          { path: "/mycommunity", label: "Community", icon: MessageSquare }
        ];

  const isCourseRelatedPage = 
    location.pathname.toLowerCase() === "/mycourse" || 
    location.pathname.toLowerCase() === "/courses" ||
    location.pathname.toLowerCase() === "/coursesdetails" ||
    location.pathname.toLowerCase().startsWith("/coursedetail") ||
    location.pathname.toLowerCase() === "/solutions" ||
    location.pathname.toLowerCase() === "/completedsolutions" ||
    location.pathname.toLowerCase() === "/mycommunity";

  const isActivePage = (path) => {
    const currentPath = location.pathname.toLowerCase();
    const itemPath = path.toLowerCase();
    
    if (currentPath === itemPath) return true;
    
    // Sub-page mapping
    if (itemPath === "/courses" && currentPath === "/coursesdetails") return true;
    if (itemPath === "/mycourse" && (currentPath === "/coursedetail" || currentPath.startsWith("/coursedetail/"))) return true;
    
    return false;
  };

  useEffect(() => {
    console.log('Current pathname:', location.pathname);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event?.target?.closest('.user-menu')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authToken'); 
    sessionStorage.clear();
    setShowLogoutModal(false);
    setShowDropdown(false);
    // Use window.location.href for a full refresh and redirect to login
    window.location.href = '/login';
  };

  const handleMobileMenuItemClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate("/");
    // Optional: Also scroll to top when navigating home
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Function to get the first letter of the name (capitalized)
  const getInitialLetter = (name) => {
    if (!name || name === "User") return 'U';
    return name.charAt(0).toUpperCase();
  };

  if (!loggedInUser) {
    return null; 
  }

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' 
          : 'bg-white/90 backdrop-blur-sm border-b border-slate-100'
      }`}>
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 h-22 md:h-16 flex items-center justify-between gap-2">
          
          {/* Logo - Left side with click handler */}
          <div 
            className="flex items-center shrink-0 cursor-pointer" 
            onClick={handleLogoClick}
          >
            <img 
              src={logo} 
              alt="Logo" 
              className="h-16 sm:h-14 md:h-16 w-auto object-contain hover:scale-105 transition-transform duration-200" 
            />
          </div>

          {/* Active Nav Item Display - Center - Hidden on mobile */}
          <div className="hidden md:block ml-13">
            <div className="text-lg lg:text-xl font-semibold">
              {activeNavItem}
            </div>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex items-center justify-end flex-1">
            {(isCourseRelatedPage && userType !== "1") && (
              <nav className="flex items-center space-x-6 lg:space-x-8">
                {menuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm lg:text-base font-medium transition-colors duration-200 relative group flex items-center gap-1.5 ${
                      isActivePage(item.path)
                        ? 'text-orange-500'  
                        : 'text-gray-700 hover:text-orange-500'  
                    }`}
                  >
                    {item.icon && <item.icon className="w-4 h-4" />}
                    <span>{item.label}</span>
                    {isActivePage(item.path) && (
                      <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-orange-500 rounded-full animate-in fade-in slide-in-from-left-1 duration-200" />
                    )}
                  </Link>
                ))}
              </nav>
            )}
          </div>

          {/* Desktop User Menu - Hidden on mobile */}
          <div className="relative user-menu shrink-0 ml-4 lg:ml-6 hidden md:block">
            <div 
              className="flex items-center space-x-1 sm:space-x-3 cursor-pointer group"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="relative">
                <div className="relative instructor-frame rounded-full overflow-hidden w-8 h-8 sm:w-10 sm:h-10">
                  <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-full w-full h-full flex items-center justify-center">
                    {/* Avatar Letter instead of Image */}
                    <span className="text-white text-sm sm:text-base font-semibold">
                      {getInitialLetter(loggedInUser?.name)}
                    </span>
                  </div>
                </div>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
              </div>
              
              <div className="block">
                <p className="text-xs sm:text-sm font-semibold text-gray-800 leading-tight">
                  {loggedInUser?.name}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-800 font-medium leading-tight hidden sm:block">
                  {loggedInUser?.email}
                </p>
              </div>
              
              <ChevronDown 
                className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : 'group-hover:rotate-180'}`}
              />
            </div>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white rounded-xl border border-slate-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 shadow-lg">
                <div className="py-1">
                  <button 
                    onClick={() => {
                      navigate(userType === "3" ? '/solutions' : '/mycourse');
                      setShowDropdown(false);
                    }}
                    className="cursor-pointer w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors duration-150 flex items-center space-x-2"
                  >
                    <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{userType === "3" ? "My Solutions" : "My Courses"}</span>
                  </button>
              
                  <button 
                    onClick={() => {
                      setShowLogoutModal(true);
                      setShowDropdown(false);
                    }}
                    className="cursor-pointer w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 flex items-center space-x-2 mt-1"
                  >
                    <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button - Right aligned with proper positioning */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-50 hover:border-amber-400 transition-all duration-200 shrink-0 ml-auto"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 md:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <div
        className={`fixed top-0 right-0 h-full w-[280px] sm:w-80 bg-white z-50 transition-transform duration-300 ease-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          
          <div className="flex items-center justify-between p-4 border-b border-slate-300 shrink-0">
            <div className="cursor-pointer" onClick={handleLogoClick}>
              <img 
                src={logo} 
                alt="Logo" 
                className="h-12 sm:h-12 w-auto object-contain" 
              />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors duration-200"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {activeNavItem && (
            <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
              <p className="text-xs text-amber-600 font-medium mb-1">Current Page</p>
              <p className="text-base font-semibold text-gray-800">{activeNavItem}</p>
            </div>
          )}

          <nav className="flex-1 py-4 sm:py-6 px-3 sm:px-4 space-y-1 sm:space-y-2 overflow-y-auto custom-scrollbar">
            {(isCourseRelatedPage || userType === "1") && menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleMobileMenuItemClick}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                  isActivePage(item.path)
                    ? 'bg-orange-50 text-orange-500 border-l-4 border-orange-500'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                }`}
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                <span>{item.label}</span>
              </Link>
            ))}
            
            <button
              onClick={() => {
                setShowLogoutModal(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-200 mt-2"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base font-medium">Logout</span>
            </button>
          </nav>

          <div className="p-4 border-t border-slate-300 shrink-0 bg-white">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="relative instructor-frame rounded-full overflow-hidden w-10 h-10">
                  <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-full w-full h-full flex items-center justify-center">
                    {/* Avatar Letter for Mobile Menu */}
                    <span className="text-white text-sm font-semibold">
                      {getInitialLetter(loggedInUser?.name)}
                    </span>
                  </div>
                </div>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {loggedInUser?.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {loggedInUser?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LogoutModal 
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </>
  );
}